import MapService from './MapService';


const CELL_PADDING = 3;
const ROOM_GAP = 2;
const ROOM_PADDING = 2;

const COLORS = {
  floor: { fill: '#FFFFFF', stroke: '#d1d5db', text: '#666666' },
  stair: { fill: '#fef3c7', stroke: '#d1d5db', text: '#92400e' },
  entrance: { fill: '#dbeafe', stroke: '#d1d5db', text: '#1e40af' },
  empty: { fill: 'transparent', stroke: '#e5e7eb', text: '#999999' },
  containerNoRooms: { fill: '#9ca3af', stroke: '#d1d5db' },
  roomAvailable: '#233f72',
  roomSelected: '#BF4B30',
  roomUnavailable: '#6b7280',
};

export function getCellSize(colCount, rowCount) {
  const maxDim = Math.max(colCount, rowCount);
  if (maxDim <= 8) return 80;
  if (maxDim <= 16) return 56;
  if (maxDim <= 24) return 40;
  return 32;
}

export function computeLayout(firstLayer, selectedSize, selectedRoom) {
  if (!firstLayer || firstLayer.length === 0)
    return { cells: [], rooms: [], totalWidth: 0, totalHeight: 0 };

  const rowCount = firstLayer.length;
  const colCount = firstLayer[0].length;
  const cellSize = getCellSize(colCount, rowCount);

  const rendered = Array.from({ length: rowCount }, () =>
    Array(colCount).fill(false)
  );
  const cells = [];
  const rooms = [];

  for (let rowIdx = 0; rowIdx < rowCount; rowIdx++) {
    for (let colIdx = 0; colIdx < colCount; colIdx++) {
      if (rendered[rowIdx][colIdx]) continue;

      const cell = firstLayer[rowIdx][colIdx];
      const x = colIdx * cellSize;
      const y = rowIdx * cellSize;

      if (!cell) {
        rendered[rowIdx][colIdx] = true;
        cells.push({
          key: `empty-${rowIdx}-${colIdx}`,
          x,
          y,
          width: cellSize,
          height: cellSize,
          type: 'empty',
          label: null,
        });
        continue;
      }

      // Calculate spanning
      let spanW = 1;
      let spanH = 1;
      if (cell.type === 'container' || cell.type === 'floor') {
        if (cell.orientation === 'horizontal') {
          spanW = cell.span || 1;
          for (let dc = 0; dc < spanW; dc++) {
            rendered[rowIdx][colIdx + dc] = true;
          }
        } else if (cell.orientation === 'vertical') {
          spanH = cell.span || 1;
          for (let dr = 0; dr < spanH; dr++) {
            rendered[rowIdx + dr][colIdx] = true;
          }
        } else {
          rendered[rowIdx][colIdx] = true;
        }
      } else {
        rendered[rowIdx][colIdx] = true;
      }

      const width = cellSize * spanW;
      const height = cellSize * spanH;

      if (cell.type === 'container') {
        const containerX = x + CELL_PADDING;
        const containerY = y + CELL_PADDING;
        const containerW = width - CELL_PADDING * 2;
        const containerH = height - CELL_PADDING * 2;
        const hasRooms = cell.rooms && cell.rooms.length > 0;
        const hasPrice =
          hasRooms &&
          !(cell.rooms.length === 1 && !cell.rooms[0].SelfStoreMonthPrice);

        if (!hasRooms || !hasPrice) {
          // Container with no usable rooms - render like a single unavailable room
          cells.push({
            key: `container-bg-empty-${rowIdx}-${colIdx}`,
            x,
            y,
            width,
            height,
            type: 'containerBg',
            label: null,
          });

          rooms.push({
            key: `room-empty-${rowIdx}-${colIdx}`,
            x: containerX,
            y: containerY,
            width: containerW,
            height: containerH,
            fill: COLORS.roomUnavailable,
            interactive: false,
            isSelected: false,
            isOccupied: true,
            isCorrectSize: false,
            room: null,
          });
        } else {
          // Container background (transparent)
          cells.push({
            key: `container-bg-${rowIdx}-${colIdx}`,
            x,
            y,
            width,
            height,
            type: 'containerBg',
            label: null,
          });

          // Subdivide into room rects
          const roomCount = cell.rooms.length;
          const useRoomInsets = roomCount > 1;
          const innerX = useRoomInsets ? containerX + ROOM_PADDING : containerX;
          const innerY = useRoomInsets ? containerY + ROOM_PADDING : containerY;
          const innerW = useRoomInsets
            ? containerW - ROOM_PADDING * 2
            : containerW;
          const innerH = useRoomInsets
            ? containerH - ROOM_PADDING * 2
            : containerH;
          const totalGaps = useRoomInsets ? (roomCount - 1) * ROOM_GAP : 0;
          const roomH = (innerH - totalGaps) / roomCount;

          cell.rooms.forEach((room, rIdx) => {
            const isOccupied = !MapService.isRoomAvailable(room);
            const isCorrectSize = selectedSize?.sizeTypes?.includes(
              room?.ItemSizeType?.Name
            );
            const isSelected = selectedRoom && room.Id === selectedRoom.Id;

            let fill = COLORS.roomUnavailable;
            let interactive = false;
            if (isSelected) {
              // Our own reservation makes the room appear occupied — always highlight it
              fill = COLORS.roomSelected;
              interactive = true;
            } else if (!isOccupied && isCorrectSize) {
              fill = COLORS.roomAvailable;
              interactive = true;
            }

            rooms.push({
              key: `room-${room.Id || room.id}-${rowIdx}-${colIdx}-${rIdx}`,
              x: innerX,
              y: innerY + rIdx * (roomH + (useRoomInsets ? ROOM_GAP : 0)),
              width: innerW,
              height: roomH,
              fill,
              interactive,
              isSelected,
              isOccupied,
              isCorrectSize,
              room,
            });
          });
        }
      } else {
        let label = null;
        if (cell.type === 'entrance') label = 'Indgang';
        if (cell.type === 'stair') label = 'Stair';

        cells.push({
          key: `${cell.type}-${rowIdx}-${colIdx}`,
          x,
          y,
          width,
          height,
          type: cell.type,
          label,
        });
      }
    }
  }

  return {
    cells,
    rooms,
    totalWidth: colCount * cellSize,
    totalHeight: rowCount * cellSize,
  };
}

export { COLORS };
